// Analytics Service - Unified wrapper for Firebase Analytics + Mixpanel
import { analytics } from '../config/firebase.js';
import { logEvent, setUserId, setUserProperties } from 'firebase/analytics';
import mixpanel from 'mixpanel-browser';

const MIXPANEL_TOKEN = '84482a7e0f54186b4f7d65c8dfe69227';

class AnalyticsService {
  constructor() {
    this.initialized = false;
  }

  init() {
    if (this.initialized) return;

    // Initialize Mixpanel with autocapture and session replay
    mixpanel.init(MIXPANEL_TOKEN, {
      debug: false,
      track_pageview: true,
      persistence: 'localStorage',
      autocapture: true,
      record_sessions_percent: 100
    });

    this.initialized = true;
  }

  // Identify user on sign-in
  identify(userId, traits = {}) {
    // Firebase Analytics
    setUserId(analytics, userId);
    setUserProperties(analytics, traits);

    // Mixpanel
    mixpanel.identify(userId);
    mixpanel.people.set({
      $email: traits.email,
      $name: traits.displayName,
      $avatar: traits.photoURL,
      experiences_count: traits.experiencesCount || 0,
      share_count: traits.shareCount || 0,
      theme: traits.theme || 'default',
      country: traits.country,
      signed_in: true,
      ...traits
    });
  }

  // Track custom event to both platforms
  track(eventName, properties = {}) {
    if (!this.initialized) {
      this.init();
    }

    try {
      // Firebase Analytics (GA4)
      if (analytics) {
        logEvent(analytics, eventName, properties);
      }

      // Mixpanel
      mixpanel.track(eventName, properties);
    } catch (error) {
      console.warn('Analytics tracking error:', error);
    }
  }

  // Reset on sign-out
  reset() {
    try {
      if (this.initialized) {
        mixpanel.reset();
      }
    } catch (error) {
      console.warn('Analytics reset error:', error);
    }
  }

  // ============ CORE EVENTS ============

  trackPageView(pageName = 'home') {
    this.track('page_view', {
      page: pageName,
      referrer: document.referrer
    });
  }

  trackSignIn(userId, method = 'google') {
    this.track('sign_in', {
      user_id: userId,
      login_method: method,
      success: true
    });
  }

  trackSignOut() {
    this.track('sign_out');
    this.reset();
  }

  trackSignUp(userId, email, method = 'google') {
    this.track('sign_up', {
      user_id: userId,
      email: email,
      signup_method: method
    });
  }

  // ============ EVENT MANAGEMENT ============

  trackEventCreated(event) {
    const startDate = new Date(2026, event.startMonth, event.startDay);
    const endDate = new Date(2026, event.endMonth, event.endDay);
    const durationDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

    this.track('event_created', {
      event_name: event.name,
      category: event.category,
      duration_days: durationDays,
      is_multi_day: durationDays > 1,
      start_month: event.startMonth,
      start_day: event.startDay
    });
  }

  trackEventDeleted(event) {
    this.track('event_deleted', {
      event_name: event.name,
      category: event.category
    });
  }

  trackEventMoved(eventName, fromDate, toDate) {
    this.track('event_moved', {
      event_name: eventName,
      from_date: fromDate,
      to_date: toDate
    });
  }

  trackEventResized(eventName, newDurationDays) {
    this.track('event_resized', {
      event_name: eventName,
      new_duration_days: newDurationDays
    });
  }

  trackEventSyncedGoogle(eventName) {
    this.track('event_synced_google', {
      event_name: eventName
    });
  }

  // ============ ENGAGEMENT EVENTS ============

  trackCalendarShared(calendarId, method = 'image') {
    this.track('calendar_shared', {
      calendar_id: calendarId,
      method: method
    });
  }

  trackThemedCalendarOpened(calendarId, calendarName) {
    this.track('themed_calendar_opened', {
      calendar_id: calendarId,
      calendar_name: calendarName
    });
  }

  trackThemedCalendarAdded(calendarId, eventCount) {
    this.track('themed_calendar_added', {
      calendar_id: calendarId,
      event_count: eventCount
    });
  }

  trackActivitySelected(activityName, category) {
    this.track('activity_selected', {
      activity_name: activityName,
      category: category
    });
  }

  trackThemeChanged(themeId, previousTheme) {
    this.track('theme_changed', {
      theme_id: themeId,
      previous_theme: previousTheme
    });
  }

  trackInviteCreated(eventName, attendeeCount = 0) {
    this.track('invite_created', {
      event_name: eventName,
      attendee_count: attendeeCount
    });
  }

  trackInviteLinkCopied(eventName) {
    this.track('invite_link_copied', {
      event_name: eventName
    });
  }

  trackInviteImageDownloaded(eventName, style) {
    this.track('invite_image_downloaded', {
      event_name: eventName,
      style: style // 'minimal' | 'fun' | 'elegant'
    });
  }

  trackInviteImageShared(eventName, style) {
    this.track('invite_image_shared', {
      event_name: eventName,
      style: style
    });
  }

  trackInviteAttendeeAdded(eventName, attendeeCount) {
    this.track('invite_attendee_added', {
      event_name: eventName,
      attendee_count: attendeeCount
    });
  }

  trackInviteViewed(eventName) {
    this.track('invite_viewed', {
      event_name: eventName
    });
  }

  trackInviteAccepted(eventName) {
    this.track('invite_accepted', {
      event_name: eventName
    });
  }

  // ============ RSVP EVENTS ============

  trackRsvpCreated(eventId, eventName, source = 'web') {
    this.track('rsvp_created', {
      event_id: eventId,
      event_name: eventName,
      source,
    });
  }

  trackRsvpUpdated(eventId, oldStatus, newStatus) {
    this.track('rsvp_updated', {
      event_id: eventId,
      old_status: oldStatus,
      new_status: newStatus,
    });
  }

  trackRsvpCancelled(eventId) {
    this.track('rsvp_cancelled', {
      event_id: eventId,
    });
  }

  trackGuestListViewed(eventId, attendeeCount) {
    this.track('guest_list_viewed', {
      event_id: eventId,
      attendee_count: attendeeCount,
    });
  }

  // ============ UI/UX EVENTS ============

  trackDrawerOpened(drawer) {
    this.track('drawer_opened', {
      drawer: drawer // 'activities' | 'calendars'
    });
  }

  trackModalOpened(modal) {
    this.track('modal_opened', {
      modal: modal // 'agenda' | 'legend' | 'help' | 'about'
    });
  }

  trackWeekendSettingChanged(newValue) {
    this.track('weekend_setting_changed', {
      new_value: newValue // 'friday' | 'saturday'
    });
  }

  trackHolidayCountryChanged(country) {
    this.track('holiday_country_changed', {
      country: country
    });
  }

  // ============ MILESTONE EVENTS ============

  trackMilestoneReached(eventCount) {
    this.track('milestone_reached', {
      event_count: eventCount
    });
  }

  trackSignInPromptShown(promptCount) {
    this.track('sign_in_prompt_shown', {
      prompt_count: promptCount
    });
  }

  trackSignInPromptDismissed(promptCount) {
    this.track('sign_in_prompt_dismissed', {
      prompt_count: promptCount
    });
  }

  // ============ CONVERSION EVENT ============

  trackConversion(type, value = null) {
    this.track('conversion', {
      conversion_type: type,
      conversion_value: value
    });
  }
}

// Export singleton instance
export const analyticsService = new AnalyticsService();

export default analyticsService;
